import { css } from '@adornis/chemistry/directives/css.js';
import { DesignDemo } from '@adornis/design-demo/client/DesignDemo.js';
import { stateful, useEffect } from '@adornis/functional-lit/stateful.js';
import { makeRouter } from '@adornis/router/client/make-router.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { addPageHeaderAdditional, PageHeader, removePageHeaderAdditional } from '@martinus/styles/client/PageHeader.js';
import { PageLoader } from '@martinus/styles/client/PageLoader.js';
import { PageTabs } from '@martinus/styles/client/PageTabs.js';
import { html } from 'lit';
import '../../healthinsurance/client/sg-healthinsurance-dataview.js';
import { NoPermission } from '../../permissions/client/NoPermission.js';
import { useMartinusABAC } from '../../permissions/client/useMartinusAbac.js';
import '../../quittungen/client/sg-quittungen-data-view.js';
import { SalaryTableForm } from '../../salary-table/client/salary-table-form.js';
import { AdminDashboardActions } from '../AdminDashboardPolicy.js';
import { AdminDashboard } from './AdminDashboard.js';
import { adminAccountingRouter, SGAdminDashboardAccountingRouterPage } from './sg-admin-dashboard-accounting-router.js';
import { SGGeneralConfig } from './sg-admin-dashboard-general-setting.js';
import { SGAdminDashboardTemplateFiles } from './sg-admin-dashboard-template-files.js';

enum AdminDashboardTabs {
  MAIN_CONFIG = 'Generelle Einstellungen',
  TEMPLATES = 'Templates',
  ACCOUNTING = 'Buchhaltung',
  HEALTH_INSURANCE = 'Krankenkassen',
  SALARY = 'Gehälter',
  ERRORS = 'Microsoft Errors',
  DESIGN = 'Design',
}

const adminSubpages = {
  mainConfig: {
    label: AdminDashboardTabs.MAIN_CONFIG,
    path: '/general',
  },
  templates: {
    label: AdminDashboardTabs.TEMPLATES,
    path: '/templates',
  },
  accounting: {
    label: AdminDashboardTabs.ACCOUNTING,
    path: '/accounting{/*}?',
  },
  healthinsurance: {
    label: AdminDashboardTabs.HEALTH_INSURANCE,
    path: '/healthinsurance',
  },
  salary: {
    label: AdminDashboardTabs.SALARY,
    path: '/salary',
  },
  design: {
    label: AdminDashboardTabs.DESIGN,
    path: '/design',
  },
} as const;

export const adminRouter = makeRouter({
  root: {
    path: '/',
    render: AdminDashboard,
    documentTitlePart: '',
  },
  [adminSubpages.mainConfig.label]: {
    path: adminSubpages.mainConfig.path,
    render: SGGeneralConfig,
  },

  [adminSubpages.templates.label]: {
    path: adminSubpages.templates.path,
    render: SGAdminDashboardTemplateFiles,
  },
  [adminSubpages.accounting.label]: {
    path: adminSubpages.accounting.path,
    render: SGAdminDashboardAccountingRouterPage,
    subRouter: adminAccountingRouter,
  },
  [adminSubpages.healthinsurance.label]: {
    path: adminSubpages.healthinsurance.path,
    render: () => html`
      <sg-healthinsurance-dataview
        ${css({ display: 'flex', flexDirection: 'column', height: '100%', marginTop: '1rem' })}
      ></sg-healthinsurance-dataview>
    `,
  },
  [adminSubpages.salary.label]: {
    path: adminSubpages.salary.path,
    render: () => html` ${SalaryTableForm()} `,
  },
  [adminSubpages.design.label]: {
    path: adminSubpages.design.path,
    render: () => DesignDemo({}),
  },
});

export const AdminDashboardRouter = stateful(() => {
  const { can: canViewDashboard, loading: canViewDashboardLoading } = useMartinusABAC({
    action: AdminDashboardActions.VIEW_ADMIN_DASHBOARD,
  });

  if (canViewDashboardLoading) {
    return PageLoader({});
  }

  if (!canViewDashboard) {
    return NoPermission({
      message: 'Du hast keine Berechtigung, auf das Admin Dashboard zuzugreifen.',
    });
  }

  useEffect(() => {
    addPageHeaderAdditional(
      0,
      PageTabs({
        tabs: Array.from(Object.values(adminSubpages)).map(subPage => ({ key: subPage.label, label: subPage.label })),
        activeKey: () => adminRouter.currentRoute.value.route.name,
        onSelect: tabKey => {
          adminRouter.goTo(tabKey as (typeof adminSubpages)[keyof typeof adminSubpages]['label']);
        },
      }),
    );
    return () => {
      removePageHeaderAdditional(0);
    };
  }, []);

  return html`
    ${PageHeader({
      title: 'Admin Dashboard',
      onTitleClick: () => {
        goTo('/admin-dashboard');
      },
    })}
    ${adminRouter.outlet.value}
  `;
});
